import "./CampaignMappingTable.scss";
import React, { useCallback, useMemo, useState } from "react";
import { Md123 } from "react-icons/md";
import { CheckBox } from "../Components/CheckBox";
import { SelfFocusingSelect } from "../Components/SelfFocusingElements";
import { BPMTable, Header } from "../Components/StickyTable/BPMTable";
import { TextToggleButton } from "../Components/TextToggleButton/TextToggleButton";
import { useMap } from "../utils/hooks/useData";
import { StateSetter } from "../utils/types";
import {
  buildOptionIndexByLevel,
  getEffectiveAccountSource,
  getEffectiveSelection,
  getLevelLabel,
  MAPPING_LEVELS,
} from "./CampaignMappingUtils";
import {
  CampaignEditsMap,
  CampaignMappingRow,
  CampaignSelectedRowsMap,
  MappingLevel,
  MappingOption,
} from "@blisspointmedia/bpm-types/dist/CampaignMappingTool";

interface CampaignMappingTableProps {
  data: CampaignMappingRow[];
  editsMap: CampaignEditsMap;
  mappingOptions: Record<MappingLevel, MappingOption[]>;
  onSelectMapping: (
    row: CampaignMappingRow,
    level: MappingLevel,
    option: MappingOption | null
  ) => void;
  selectedRows: CampaignSelectedRowsMap;
  setSelectedRows: StateSetter<CampaignSelectedRowsMap>;
}

interface FocusedCell {
  rawDataId: string;
  level: MappingLevel;
}

type NameIdMode = "Names" | "IDs";

const EMPTY_DISPLAY = "-";

const renderToggleableCell = (
  rawDataId: string,
  name: string | undefined,
  id: string | undefined,
  showAllIds: boolean,
  showRowId: Partial<Record<string, boolean>>,
  setShowRowId: (key: string, value: boolean) => void
): JSX.Element => {
  const wantsId = showAllIds !== Boolean(showRowId[rawDataId]);
  const value = wantsId ? id : name;
  const text = value || EMPTY_DISPLAY;

  return (
    <div className="toggleableCell">
      <span className="cellText">{text}</span>
      {id ? (
        <div
          className="digitsIcon"
          onMouseEnter={() => setShowRowId(rawDataId, true)}
          onMouseLeave={() => setShowRowId(rawDataId, false)}
          onClick={event => {
            event.stopPropagation();
            navigator.clipboard.writeText(id);
          }}
        >
          <Md123 />
        </div>
      ) : null}
    </div>
  );
};

const CampaignMappingTable: React.FC<CampaignMappingTableProps> = ({
  data,
  editsMap,
  mappingOptions,
  onSelectMapping,
  selectedRows,
  setSelectedRows,
}) => {
  const [focusedCell, setFocusedCell] = useState<FocusedCell | null>(null);

  const allVisibleSelected = useMemo(
    () => data.length > 0 && data.every(row => Boolean(selectedRows[row.raw_data_id])),
    [data, selectedRows]
  );

  const toggleRowSelection = useCallback(
    (row: CampaignMappingRow) => {
      setSelectedRows(current => {
        const next = { ...current };
        if (next[row.raw_data_id]) {
          delete next[row.raw_data_id];
        } else {
          next[row.raw_data_id] = row;
        }
        return next;
      });
    },
    [setSelectedRows]
  );

  const toggleSelectAllVisible = useCallback(() => {
    setSelectedRows(current => {
      if (data.length > 0 && data.every(row => Boolean(current[row.raw_data_id]))) {
        const next = { ...current };
        for (const row of data) {
          delete next[row.raw_data_id];
        }
        return next;
      }
      const next = { ...current };
      for (const row of data) {
        next[row.raw_data_id] = row;
      }
      return next;
    });
  }, [data, setSelectedRows]);

  const [showAllCampaignIds, setShowAllCampaignIds] = useState<boolean>(false);
  const [showAllAdGroupIds, setShowAllAdGroupIds] = useState<boolean>(false);
  const [showAllAdIds, setShowAllAdIds] = useState<boolean>(false);

  const [showRowCampaignId, setShowRowCampaignIdValue] = useMap<string, boolean>({});
  const [showRowAdGroupId, setShowRowAdGroupIdValue] = useMap<string, boolean>({});
  const [showRowAdId, setShowRowAdIdValue] = useMap<string, boolean>({});

  const handleCampaignToggle = useCallback(
    (key: NameIdMode) => setShowAllCampaignIds(key === "IDs"),
    []
  );
  const handleAdGroupToggle = useCallback(
    (key: NameIdMode) => setShowAllAdGroupIds(key === "IDs"),
    []
  );
  const handleAdToggle = useCallback((key: NameIdMode) => setShowAllAdIds(key === "IDs"), []);

  const optionByKeyByLevel = useMemo(() => buildOptionIndexByLevel(mappingOptions), [
    mappingOptions,
  ]);

  const headers = useMemo<Header[]>(() => {
    const renderNameIdHeader = (
      title: string,
      showAllIds: boolean,
      onToggle: (key: NameIdMode) => void
    ): JSX.Element => (
      <div className="nameIdHeader">
        <span className="nameIdHeaderTitle">{title}</span>
        <div className="nameIdHeaderToggle" onClick={event => event.stopPropagation()}>
          <TextToggleButton<NameIdMode>
            options={["Names", "IDs"]}
            selectedOption={showAllIds ? "IDs" : "Names"}
            onChange={onToggle}
            size="sm"
          />
        </div>
      </div>
    );

    const buildMapColumn = (level: MappingLevel): Header => {
      const label = `${getLevelLabel(level)} mapping`;
      const placeholder = `Select ${getLevelLabel(level).toLowerCase()}`;
      return {
        label,
        name: `map_${level}`,
        width: 200,
        nonFilterable: true,
        nonInteractive: true,
        renderer: (row: CampaignMappingRow) => {
          const edit = editsMap[row.raw_data_id];
          const selectedOption = getEffectiveSelection(row, edit, level, optionByKeyByLevel);
          const isFocused =
            focusedCell?.rawDataId === row.raw_data_id && focusedCell.level === level;

          const constraint = getEffectiveAccountSource(row, edit, optionByKeyByLevel, level);
          const options = constraint
            ? mappingOptions[level].filter(
                option =>
                  option.accountId === constraint.accountId && option.source === constraint.source
              )
            : mappingOptions[level];

          if (isFocused) {
            return (
              <SelfFocusingSelect
                menuPortalTarget={document.body}
                className="campaignMapSelect"
                value={selectedOption}
                options={options}
                placeholder={placeholder}
                onChange={selection => {
                  onSelectMapping(row, level, (selection as MappingOption | null) || null);
                }}
                onBlur={() => {
                  setFocusedCell(null);
                }}
                defaultMenuIsOpen
                isClearable
              />
            );
          }

          return (
            <div
              className="campaignMapCell"
              onClick={() => {
                setFocusedCell({ rawDataId: row.raw_data_id, level });
              }}
            >
              <span className={selectedOption ? "mappedLabel" : "placeholderLabel"}>
                {selectedOption?.label || placeholder}
              </span>
            </div>
          );
        },
      };
    };

    const baseHeaders: Header[] = [
      {
        label: "",
        name: "_select",
        width: 44,
        nonFilterable: true,
        nonInteractive: true,
        headerRenderer: () => (
          <div className="selectCell" onClick={event => event.stopPropagation()}>
            <CheckBox checked={allVisibleSelected} onCheck={toggleSelectAllVisible} />
          </div>
        ),
        renderer: (row: CampaignMappingRow) => (
          <div className="selectCell" onClick={event => event.stopPropagation()}>
            <CheckBox
              checked={Boolean(selectedRows[row.raw_data_id])}
              onCheck={() => toggleRowSelection(row)}
            />
          </div>
        ),
      },
      { label: "Analytics Raw Source", name: "analytics_raw_source", width: 150 },
      { label: "Analytics Raw Medium", name: "analytics_raw_medium", width: 150 },
      {
        label: "Analytics Campaign",
        name: "analytics_campaign_name",
        width: 220,
        headerRenderer: () =>
          renderNameIdHeader("Analytics Campaign", showAllCampaignIds, handleCampaignToggle),
        contentGetter: (row: CampaignMappingRow) =>
          row.analytics_campaign_name || row.analytics_campaign_id || "",
        renderer: (row: CampaignMappingRow) =>
          renderToggleableCell(
            row.raw_data_id,
            row.analytics_campaign_name,
            row.analytics_campaign_id,
            showAllCampaignIds,
            showRowCampaignId,
            setShowRowCampaignIdValue
          ),
      },
      {
        label: "Analytics Ad Group",
        name: "analytics_ad_group_name",
        width: 220,
        headerRenderer: () =>
          renderNameIdHeader("Analytics Ad Group", showAllAdGroupIds, handleAdGroupToggle),
        contentGetter: (row: CampaignMappingRow) =>
          row.analytics_ad_group_name || row.analytics_ad_group_id || "",
        renderer: (row: CampaignMappingRow) =>
          renderToggleableCell(
            row.raw_data_id,
            row.analytics_ad_group_name,
            row.analytics_ad_group_id,
            showAllAdGroupIds,
            showRowAdGroupId,
            setShowRowAdGroupIdValue
          ),
      },
      {
        label: "Analytics Ad",
        name: "analytics_ad_name",
        width: 220,
        headerRenderer: () => renderNameIdHeader("Analytics Ad", showAllAdIds, handleAdToggle),
        contentGetter: (row: CampaignMappingRow) =>
          row.analytics_ad_name || row.analytics_ad_id || "",
        renderer: (row: CampaignMappingRow) =>
          renderToggleableCell(
            row.raw_data_id,
            row.analytics_ad_name,
            row.analytics_ad_id,
            showAllAdIds,
            showRowAdId,
            setShowRowAdIdValue
          ),
      },
    ];

    MAPPING_LEVELS.forEach(level => baseHeaders.push(buildMapColumn(level)));

    return baseHeaders;
  }, [
    allVisibleSelected,
    editsMap,
    focusedCell,
    handleAdGroupToggle,
    handleAdToggle,
    handleCampaignToggle,
    mappingOptions,
    onSelectMapping,
    optionByKeyByLevel,
    selectedRows,
    setShowRowAdGroupIdValue,
    setShowRowAdIdValue,
    setShowRowCampaignIdValue,
    showAllAdGroupIds,
    showAllAdIds,
    showAllCampaignIds,
    showRowAdGroupId,
    showRowAdId,
    showRowCampaignId,
    toggleRowSelection,
    toggleSelectAllVisible,
  ]);

  return (
    <div className="campaignMappingTable">
      <BPMTable
        className="campaignMappingBpmTable"
        data={data}
        headers={headers}
        filterBar={false}
        rowHeight={56}
        headerHeight={64}
        emptyDisplay="-"
        noRowsRenderer={() => <div className="noRows">No rows to display.</div>}
      />
    </div>
  );
};

export default CampaignMappingTable;
